import { Injectable } from '@angular/core';

declare global {
  interface Window {
    grecaptcha?: GrecaptchaEnterpriseHost;
    /** Set by RecaptchaService while loading; Google calls this when the API is ready. */
    __recaptchaOnload?: () => void;
  }
}

/** Minimal typing for Enterprise explicit render + helpers */
export interface GrecaptchaEnterpriseHost {
  enterprise?: {
    ready?: (callback: () => void) => void;
    render?: (
      container: HTMLElement,
      parameters: Record<string, unknown>
    ) => number;
    getResponse?: (widgetId?: number) => string;
    reset?: (widgetId?: number) => void;
  };
}

export interface RecaptchaRenderCallbacks {
  onSuccess: (token: string) => void;
  onExpired: () => void;
  onError: () => void;
}

const SCRIPT_ID = 'google-recaptcha-enterprise-script';
const ONLOAD_CALLBACK = '__recaptchaOnload';

@Injectable({ providedIn: 'root' })
export class RecaptchaService {
  private readyPromise: Promise<void> | null = null;

  /**
   * Loads the Enterprise script once and resolves when the API is ready.
   * Uses Google's `onload` callback (see enterprise.js?onload=...&render=explicit) so we
   * do not rely on polling or guessing when `enterprise.render` becomes available.
   */
  ensureLoaded(): Promise<void> {
    if (this.isRenderAvailable()) {
      return Promise.resolve();
    }
    if (this.readyPromise) {
      return this.readyPromise;
    }
    this.readyPromise = this.loadRecaptchaEnterprise();
    return this.readyPromise;
  }

  render(
    container: HTMLElement,
    siteKey: string,
    action: string,
    callbacks: RecaptchaRenderCallbacks
  ): number | null {
    const enterprise = window.grecaptcha?.enterprise;
    if (
      !enterprise ||
      typeof enterprise.render !== 'function' ||
      !container ||
      !siteKey
    ) {
      return null;
    }

    return enterprise.render(container, {
      sitekey: siteKey,
      action,
      callback: callbacks.onSuccess,
      'expired-callback': callbacks.onExpired,
      'error-callback': callbacks.onError,
    });
  }

  getResponse(widgetId: number | null): string {
    if (widgetId === null) {
      return '';
    }
    const getResponse = window.grecaptcha?.enterprise?.getResponse;
    if (typeof getResponse !== 'function') {
      return '';
    }
    return getResponse.call(window.grecaptcha.enterprise, widgetId) ?? '';
  }

  reset(widgetId: number | null): void {
    if (widgetId === null) {
      return;
    }
    const resetFn = window.grecaptcha?.enterprise?.reset;
    if (typeof resetFn !== 'function') {
      return;
    }
    resetFn.call(window.grecaptcha.enterprise, widgetId);
  }

  private isRenderAvailable(): boolean {
    return typeof window.grecaptcha?.enterprise?.render === 'function';
  }

  private loadRecaptchaEnterprise(): Promise<void> {
    return new Promise((resolve, reject) => {
      const finalizeOk = () => {
        if (this.isRenderAvailable()) {
          resolve();
        } else {
          this.readyPromise = null;
          reject(
            new Error('reCAPTCHA Enterprise render() is not available.')
          );
        }
      };

      const finalizeErr = (err: Error) => {
        this.readyPromise = null;
        reject(err);
      };

      const existing = document.getElementById(
        SCRIPT_ID
      ) as HTMLScriptElement | null;
      if (existing) {
        const onDone = () => {
          existing.setAttribute('data-loaded', 'true');
          finalizeOk();
        };
        if (existing.getAttribute('data-loaded') === 'true') {
          onDone();
          return;
        }
        const el = existing as HTMLScriptElement & { complete?: boolean };
        if (el.complete) {
          onDone();
          return;
        }
        existing.addEventListener('load', onDone);
        existing.addEventListener('error', () =>
          finalizeErr(new Error('reCAPTCHA script failed to load'))
        );
        return;
      }

      window[ONLOAD_CALLBACK] = () => {
        delete window[ONLOAD_CALLBACK];
        document
          .getElementById(SCRIPT_ID)
          ?.setAttribute('data-loaded', 'true');
        finalizeOk();
      };

      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = `https://www.google.com/recaptcha/enterprise.js?onload=${ONLOAD_CALLBACK}&render=explicit`;
      script.async = true;
      script.defer = true;
      script.onerror = () =>
        finalizeErr(new Error('Failed to load reCAPTCHA script'));
      document.head.appendChild(script);
    });
  }
}
